import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { deleteTemplate, getTemplates } from "../api/selectTemplate";

import { DeleteIconButton } from "../components/Button";
import FeedbackSnackbar from "../components/FeedbackSnackbar";
import Loading from "../components/Loading";
import TemplateListHeader from "../components/TemplateListHeader";

import { useConfig } from "../contexts/ConfigContext";

function SelectBaseTemplate() {
  const [templates, setTemplates] = useState([]);
  const [templates_display, setTemplatesDisplay] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") === "edit" ? "edit" : "duplicate";
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const showSnackbar = (message, severity = "success") => {
    setFeedback({ open: true, message, severity });
  };

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTemplates();
      const response_sorted = response.sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setTemplates(response_sorted);
      setTemplatesDisplay(response_sorted);
    } catch (error) {
      console.error("テンプレートの取得時にエラーが発生しました:", error);
      showSnackbar("テンプレートの読み込みに失敗しました。", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleFilterChange = useCallback((filteredTemplates) => {
    setTemplatesDisplay(filteredTemplates);
  }, []);

  const handleTemplateClick = (templatePublicId) => {
    if (mode === "edit") {
      navigate(`/templates/${templatePublicId}/edit`);
      return;
    }

    navigate(
      `/templates/new?baseTemplate=${encodeURIComponent(templatePublicId)}`
    );
  };

  const handleDeleteClick = (e, template) => {
    e.stopPropagation();
    setTemplateToDelete(template);
    setConfirmDeleteDialogOpen(true);
  };

  const handleCloseConfirmDeleteDialog = () => {
    setConfirmDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    setLoading(true);
    try {
      await deleteTemplate(templateToDelete.public_id);
      setConfirmDeleteDialogOpen(false);

      const newTemplates = templates.filter((t) => t.id !== templateToDelete.id);
      setTemplates(newTemplates);
      setTemplatesDisplay((prev) =>
        prev.filter((t) => t.id !== templateToDelete.id)
      );

      showSnackbar(`「${templateToDelete.name}」を削除しました。`, "success");
    } catch (error) {
      console.error("Failed to delete template:", error);
      showSnackbar(`削除に失敗しました: ${error.message}`, "error");
    } finally {
      setLoading(false);
      setTemplateToDelete(null);
    }
  };

  const config = useConfig();
  const tagSelectorFeatureEnabled = config?.features?.enableTagSelector ?? false;

  if (loading) {
    return <Loading />;
  }

  return (
    <Stack spacing={2}>
      <TemplateListHeader
        templates={templates}
        onFilterChange={handleFilterChange}
        tagSelectorEnabled={tagSelectorFeatureEnabled}
      />

      <Paper sx={{ p: 1.5 }}>
        {templates_display.length === 0 ? (
          <Alert severity="info">条件に一致するテンプレートはありません。</Alert>
        ) : mode === "duplicate" ? (
          <List disablePadding>
            {templates_display.map((template) => (
              <ListItemButton
                key={template.id}
                onClick={() => handleTemplateClick(template.public_id)}
                divider
              >
                <ListItemText primary={template.name} />
              </ListItemButton>
            ))}
          </List>
        ) : (
          <TableContainer>
            <Table>
              <TableBody>
                {templates_display.map((template) => (
                  <TableRow
                    key={template.id}
                    hover
                    onClick={() => handleTemplateClick(template.public_id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell sx={{ py: 1.25, maxWidth: 0 }}>
                      <Typography
                        component="span"
                        noWrap
                        sx={{ display: "block" }}
                      >
                        {template.name}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ width: 48, py: 0.5 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DeleteIconButton
                        size="small"
                        aria-label="削除"
                        onClick={(e) => handleDeleteClick(e, template)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={confirmDeleteDialogOpen}
        onClose={handleCloseConfirmDeleteDialog}
      >
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            テンプレート「{templateToDelete?.name}」を本当に削除しますか？
            この操作は元に戻せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDeleteDialog}>キャンセル</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            削除する
          </Button>
        </DialogActions>
      </Dialog>

      <FeedbackSnackbar
        open={feedback.open}
        message={feedback.message}
        severity={feedback.severity}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
      />
    </Stack>
  );
}

export default SelectBaseTemplate;
